// src/Components/TableData.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, Box, Stack, Chip } from '@mui/material';
import { MaterialReactTable, MRT_ExpandAllButton } from 'material-react-table';

const FONT_FAMILY =
  "'Montserrat','Inter',system-ui,-apple-system,'Segoe UI',Roboto,Arial,sans-serif";

const STATUS_COL_KEYS = ['statut', 'Status', 'Statut'];

const STATUS_STYLES = {
  // Logistique
  Disponible: { bg: '#e8f5e9', fg: '#1b5e20' },
  Utilisé: { bg: '#e3f2fd', fg: '#0d47a1' },
  Perdu: { bg: '#ffebee', fg: '#b71c1c' },
  Endommagé: { bg: '#fff3e0', fg: '#e65100' },
  // Transport (compat)
  Réserver: { bg: '#d1ecf1', fg: '#0c5460' },
  'En cours': { bg: '#e0f2ff', fg: '#007bff' },
  Annulé: { bg: '#f8d7da', fg: '#dc3545' },
  Réalisé: { bg: '#e6f4ea', fg: '#2e7d32' },
  Facturé: { bg: '#fff8e1', fg: '#795548' },
};

const getStatusStyle = (status) => {
  const key = String(status ?? '').trim();
  return STATUS_STYLES[key] ?? { bg: '#f5f5f5', fg: '#616161' };
};

const findStatusKey = (cols) => {
  const hit = cols.find(
    (c) => c && c.accessorKey && STATUS_COL_KEYS.includes(String(c.accessorKey)),
  );
  return hit ? hit.accessorKey : undefined;
};

const DataGridTable = ({ data, columns, fullScreen = false }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [rowSelection, setRowSelection] = useState({});

  const statusKey = useMemo(() => findStatusKey(columns), [columns]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? 'dark' : 'light',
          primary: { main: darkMode ? '#90caf9' : '#1976d2' },
        },
        typography: { fontFamily: FONT_FAMILY },
      }),
    [darkMode],
  );

  // persist selected rows for external modals
  useEffect(() => {
    const pk = columns[0]?.accessorKey;
    if (!pk) return;

    const selected = Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => data.find((r) => String(r[pk]) === id))
      .filter(Boolean);

    localStorage.setItem('selectedRows', JSON.stringify(selected));
  }, [rowSelection, columns, data]);

  // decorate status column with a Chip
  const enhancedColumns = useMemo(() => {
    if (!statusKey) return columns;
    return columns.map((col) =>
      col.accessorKey === statusKey
        ? {
          ...col,
          Cell: ({ cell }) => {
            const v = cell.getValue();
            const { bg, fg } = getStatusStyle(v);
            return (
              <Chip
                label={String(v ?? '')}
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  height: 28,
                  borderRadius: 1,
                  bgcolor: bg,
                  color: fg,
                  '& .MuiChip-label': { px: 1.25, lineHeight: 1.4 },
                }}
              />
            );
          },
        }
        : col,
    );
  }, [columns, statusKey]);

  const rowBgFor = (rowObj) => {
    const value = statusKey ? rowObj?.[statusKey] : undefined;
    return getStatusStyle(value).bg;
  };

  return (
    <ThemeProvider theme={theme}>
      <MaterialReactTable
        style={{
          borderRadius: 8,
          position: fullScreen ? 'fixed' : 'relative',
          inset: fullScreen ? 0 : 'unset',
          zIndex: fullScreen ? 99999 : 'auto',
        }}
        columns={enhancedColumns}
        data={data}
        enableClickToCopy
        enableGrouping
        enableExpanding
        enableDensityToggle
        enableGlobalFilter
        enableColumnResizing
        enableColumnOrdering
        enableRowSelection
        enableColumnDragging
        enableFullScreenToggle={false}
        enablePagination
        enableFilters
        enableHiding
        enableSorting
        enableBottomToolbar
        enableMultiRowSelection

        muiTableContainerProps={{
          sx: {
            flex: 1,
            height: fullScreen ? '100%' : undefined,
            overflowY: 'auto',
          },
        }}
        muiTableBodyProps={{
          sx: { overflowY: 'auto' }
        }}

        displayColumnDefOptions={{
          'mrt-row-expand': {
            Header: ({ table }) => (
              <Stack direction="row" alignItems="center" gap={1}>
                <MRT_ExpandAllButton table={table} />
                <Box component="span" sx={{ fontFamily: FONT_FAMILY }}>
                  Groups
                </Box>
              </Stack>
            ),
            size: 100,
          },
        }}
        getRowId={(row) => String(row[columns[0]?.accessorKey])}
        onRowSelectionChange={setRowSelection}
        state={{ rowSelection }}
        initialState={{
          pagination: { pageIndex: 0, pageSize: 10 },
          density: 'spacious',
          sorting: [
            {
              id: columns[0]?.accessorKey,
              desc: true,
            },
          ],

        }}
        muiTablePaperProps={{
          sx: {
            ...(fullScreen && {
              position: 'fixed',     // 🔥 use fixed instead of absolute
              top: 0,
              left: 0,
              width: '120vw',        // ✅ correct width
              height: '100vh',
              zIndex: 9999,
              borderRadius: 0,
              boxShadow: 'none',
              backgroundColor: 'white',
              margin: 0,
              padding: 0,
            }),
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }
        }}

        muiTableProps={{ sx: { fontFamily: FONT_FAMILY } }}
        muiTableHeadCellProps={{
          sx: { fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: '0.86rem' },
        }}
        muiTableBodyCellProps={{
          sx: { fontFamily: FONT_FAMILY, fontSize: '0.92rem' },
        }}
        muiTableBodyRowProps={({ row }) => ({
          sx: {
            backgroundColor: rowBgFor(row.original),
            '&:nth-of-type(odd)': { filter: 'brightness(0.99)' },
            '&:hover': { filter: 'brightness(0.97)', transition: 'filter 120ms ease' },
          },
        })}
      />
    </ThemeProvider>
  );
};

export default DataGridTable;
